import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { Downloading, Receipt } from "@mui/icons-material";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { getUserActivityTableColumns } from "../../pages/administration/constants";
import {
  AccountService,
  AnalyticsService,
  FinanceService,
  HumanResourceService,
  ProjectService,
  PurchaseService,
} from "../../services";
import { DELETE_USER_PAYMENT, EDIT_USER_PAYMENT } from "../../constants";
import { AccountTypes, derivePdfColumns, ExpenseCategories, TransactionTypes, useHasPermission } from "../../utils";
import { getAccountName } from "../../utils/formatters";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const UserActivities = ({ name = "", userId, accountId, onSuccess = () => {}, reloadData }) => {
  const [userTransactions, setUserTransactions] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "user-activities",
    defaults: { filter: "expense", sortModel: [{ field: "date", sort: "desc" }] },
    filterValues: ["expense", "transaction"],
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    transactionId: null,
  });

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEditTransaction = useHasPermission(EDIT_USER_PAYMENT);
  const hasDeleteTransaction = useHasPermission(DELETE_USER_PAYMENT);

  const fetchData = useCallback(
    async (analyticsFilters = {}) => {
      setLoading(true);
      try {
        const { data: transactions } = await AnalyticsService.getUserAnalytics({
          ...analyticsFilters,
          userId,
        });

        const projectIds = Array.from(new Set(transactions?.rows?.map((item) => item.projectId)));
        const categoryIds = Array.from(new Set(transactions?.rows?.map((item) => item.categoryId)));
        const accountIds = Array.from(
          new Set(
            transactions.rows
              .map((item) => [item.fromAccountId, item.toAccountId])
              .flat()
              .filter(Boolean),
          ),
        );
        const destinationEmployeeIds = Array.from(
          new Set(
            transactions.rows
              .filter((item) => item.destinationType === "employees")
              .map((item) => item.destinationId)
              .filter(Boolean),
          ),
        );
        const destinationVendorIds = Array.from(
          new Set(
            transactions.rows
              .filter((item) => item.destinationType === "vendors")
              .map((item) => item.destinationId)
              .filter(Boolean),
          ),
        );

        let projects = { rows: [] };
        let categories = { rows: [] };
        let accounts = { rows: [] };
        let employees = { rows: [] };
        let vendors = { rows: [] };

        if (projectIds.length > 0) {
          const { data } = await ProjectService.getProjectList({
            ids: projectIds.join(","),
            limit: projectIds.length,
            includeDeleted: true,
          });
          projects = data;
        }

        if (categoryIds.length > 0) {
          const { data } = await FinanceService.getCategories({
            ids: categoryIds.join(","),
            limit: categoryIds.length,
            includeGlobal: true,
          });
          categories = data;
        }

        if (accountIds.length > 0) {
          const { data } = await AccountService.getAccounts({
            ids: accountIds.join(","),
            limit: accountIds.length,
            includeOwnerDetails: true,
            includeRestrictedTypes: true,
            includeDeleted: true,
          });
          accounts = data;
        }

        if (destinationEmployeeIds.length > 0) {
          const { data } = await HumanResourceService.getEmployees({
            ids: destinationEmployeeIds.join(","),
            limit: destinationEmployeeIds.length,
            includeDeleted: true,
          });
          employees = data;
        }

        if (destinationVendorIds.length > 0) {
          const { data } = await PurchaseService.getVendors({
            ids: destinationVendorIds.join(","),
            limit: destinationVendorIds.length,
          });
          vendors = data;
        }

        //   mapping transactions with project and account details
        const mappedTransactions = transactions.rows.map((transaction) => {
          const projectDetails = projects.rows.find((project) => project.id === transaction.projectId);
          const categoryDetails = categories.rows.find((category) => category.id === transaction.categoryId);
          const destinationDetails =
            transaction.destinationType === "employees"
              ? employees.rows.find((emp) => emp.id === transaction.destinationId)
              : transaction.destinationType === "vendors"
                ? vendors.rows.find((ven) => ven.id === transaction.destinationId)
                : null;
          const fromAccount = accounts.rows.find((account) => account.id === transaction.fromAccountId);
          const toAccount = accounts.rows.find((account) => account.id === transaction.toAccountId);
          const fromAccountName = getAccountName(fromAccount?.type, fromAccount);
          const toAccountName = getAccountName(toAccount?.type, toAccount);

          return {
            ...transaction,
            projectDetails,
            categoryDetails,
            destinationDetails,
            fromAccount,
            fromAccountName,
            toAccount,
            toAccountName,
          };
        });

        setUserTransactions(mappedTransactions);
        setTotalCount(transactions.count);
      } catch (error) {
        toast.error("Failed to fetch user transactions");
        console.error(`~ fetchData ~ error:`, error);
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      type: selectedFilter,
      description: searchText,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, dateRange, reloadData]);

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Ensure day-level inclusive filtering for user analytics API.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[String(selectedFilter)] || "User Activities";
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = rows.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [key, value === undefined || value === null ? "" : String(value)]),
        ),
      );

      const pdfDocument = (
        <Listing title={pdfTitle} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: pdfTitle,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const navigateToExpense = async (expenseId, category) => {
    try {
      const { data: expense } = await FinanceService.getExpense(expenseId);

      switch (category) {
        case ExpenseCategories.WORKER:
          navigate(`/expense/worker/${expense?.work?.id}`);
          break;

        case ExpenseCategories.VENDOR:
          navigate(`/expense/vendor/${expense?.purchaseInvoice?.id}`);
          break;

        case ExpenseCategories.SUBCONTRACTOR:
          navigate(`/expense/subcontractor/${expense?.id}`);
          break;

        default:
          break;
      }
    } catch (error) {
      toast.error("Failed to load expense details");
      console.error("~ navigateToExpense ~ error:", error);
    }
  };

  const onViewClick = async (params) => {
    const { id, type, activityType } = params.row;

    switch (type) {
      case "transaction":
        navigate(`/transaction/${id}`);
        break;

      case "expense":
        switch (activityType) {
          case ExpenseCategories.WORKER:
          case ExpenseCategories.VENDOR:
          case ExpenseCategories.SUBCONTRACTOR:
            await navigateToExpense(id, activityType);
            break;

          case ExpenseCategories.OTHER:
            navigate(`/expense/${id}`);
            break;

          default:
            break;
        }
        break;

      default:
        break;
    }
  };

  const openDeleteModal = (transactionData) => {
    setDeleteModal({
      isOpen: true,
      transactionId: transactionData?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      transactionId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteTransaction(deleteModal.transactionId);
      toast.success("Transaction deleted successfully");
      closeDeleteModal();
      onSuccess?.();
    } catch (error) {
      toast.error("Failed to delete transaction");
      console.error(`~ handleDeleteTransaction ~ error:`, error);
    }
  };

  const handleActions = async (transactionData, actionType) => {
    switch (actionType) {
      case "edit":
        const { id, transactionType, toAccountId } = transactionData;
        switch (transactionType) {
          case TransactionTypes.ACCOUNT:
            navigate(`/transaction/edit/${id}?userId=${userId}`);
            break;
          case TransactionTypes.WORKER:
            const { data: toAccount } = await AccountService.getAccountById(toAccountId);
            navigate(`/transaction/edit/${id}?workerId=${toAccount?.ownerEmployee?.id}`);
            break;
          case TransactionTypes.VENDOR:
            const { data: toAccountVendor } = await AccountService.getAccountById(toAccountId);
            navigate(`/transaction/edit/${id}?vendorId=${toAccountVendor?.ownerVendor?.id}`);
            break;
          default:
            break;
        }
        break;
      case "delete":
        openDeleteModal(transactionData);
        break;
      default:
        break;
    }
  };

  const filterOptions = [
    { label: "Entries", value: "expense", icon: <Receipt /> },
    {
      label: "Transactions",
      value: "transaction",
      icon: <Downloading />,
    },
  ];

  const pdfTitleMap = {
    expense: `Expenses : ${name}`,
    transaction: `Transactions : ${name}`,
  };

  const columnHeaders = getUserActivityTableColumns(handleActions, hasEditTransaction, hasDeleteTransaction, selectedFilter);

  const rows = userTransactions
    ? userTransactions.map((transaction, index) => {
        const isExpense = transaction.type === "expense";
        const isTransaction = transaction.type === "transaction";

        let activityType = "";
        let color = "inherit";

        // Normal Misc. Expense without destination
        if (isExpense && !transaction?.destinationId) {
          activityType = `${transaction.categoryDetails?.name} (Other Expense)`;
          color = "red";
        }
        // Expense with destination (like employee or vendor)
        else if (isExpense && transaction.destinationId) {
          activityType = transaction.categoryDetails?.name;
          color = "red";
        }
        // Transaction: Outbound,no toAccountId (transaction made against expense)
        else if (
          (isTransaction && !transaction?.toAccountId && transaction?.fromAccountId === accountId) ||
          (isTransaction && !transaction?.toAccountId)
        ) {
          activityType = "Expense Transaction";
          color = "red";
        }
        // Transaction: Outbound, both toAccountId and fromAccountId exist and current user is the sender
        else if (isTransaction && transaction.toAccountId && transaction.fromAccountId === accountId) {
          const otherAccountType = transaction.toAccount?.type;
          activityType = AccountTypes[otherAccountType];
          color = "red"; // Transaction: Inbound, both toAccountId and fromAccountId exist and current user is the creator
        } else if (isTransaction && transaction.toAccountId && transaction.fromAccountId && transaction.createdById === userId) {
          const otherAccountType = transaction.fromAccount?.type;
          activityType = AccountTypes[otherAccountType];
          color = "red";
          // Transaction: Inbound Account Transaction
        } else if (isTransaction && transaction.toAccountId === accountId && transaction.fromAccountId) {
          activityType = "Account Transaction";
          color = "green";
          // Transaction: Outbound Account Transaction
        } else if (isTransaction && transaction.fromAccountId === accountId && transaction.fromAccountId) {
          activityType = "Account Transaction";
          color = "red";
        } else {
          activityType = transaction.type;
        }

        return {
          sl: index + 1 + paginationState.page * paginationState.pageSize,
          id: transaction.id,
          date: transaction?.date ? moment(transaction.date).format("DD MMM YYYY") : "-",
          project: transaction?.projectDetails?.name,
          description: transaction?.description,
          activityType: activityType,
          amount: transaction?.amount,
          meta: transaction?.meta || {},
          color: color,
          type: transaction.type,
          toAccountId: transaction.toAccountId,
          toAccountName: transaction?.toAccountName,
          fromAccountName: transaction?.fromAccountName,
          transactionType: transaction?.transactionType,
        };
      })
    : [];

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton
          // Search data
          placeholder="Search"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this transaction?"
      />
    </>
  );
};
